/**
 * Shared Excel header helpers for sync/import sample workbooks
 * (tickets, promo codes, article catalogue, …).
 *
 * Pattern from TicketsRawImporter: i18n column labels + hover comments on
 * the header row, and multi-language aliases for parsing uploads.
 */
import * as XLSX from '@e965/xlsx';

/** Locales used when building header aliases. */
export const IMPORT_HEADER_LANGS = ['fr', 'en', 'de', 'it'];

/**
 * @param {*} value
 * @returns {string}
 */
export const sanitizeHeaderCell = value => {
    if (value === null || typeof value === 'undefined')
        return '';

    return String(value).replace(/[\u00A0\u202F]/g, ' ').trim();
};

/**
 * Collapse whitespace/punctuation so "Stock type" and "stock_type" match.
 * @param {*} label
 * @returns {string}
 */
export const normalizeHeaderLabel = label =>
    sanitizeHeaderCell(label).toLowerCase().replace(/[\s._-]+/g, '');

/**
 * Remove parenthetical hints such as `(0|1)` or `(article|variant|none)`.
 * @param {*} label
 * @returns {string}
 */
export const stripHeaderHints = label =>
    sanitizeHeaderCell(label).replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Build a map of normalized header text → internal column key.
 *
 * @param {object} options
 * @param {string[]} options.columnKeys Internal keys (e.g. `['nb', 'type']`).
 * @param {(key: string, lang: string) => string} options.labelFor Key → label in a language.
 * @param {string[]} [options.langs] Languages to register (default FR/EN/DE/IT).
 * @param {Record<string, string>} [options.legacyLabels] English/legacy labels → key.
 * @param {Array<[string, string]>} [options.extraAliases] Extra [label, key] pairs.
 * @returns {Record<string, string>}
 */
export const buildHeaderAliases = ({
    columnKeys,
    labelFor,
    langs = IMPORT_HEADER_LANGS,
    legacyLabels = {},
    extraAliases = [],
} = {}) => {
    const aliases = {};
    const add = (label, key) => {
        const normalized = normalizeHeaderLabel(label);
        if (normalized)
            aliases[normalized] = key;
        const stripped = normalizeHeaderLabel(stripHeaderHints(label));
        if (stripped)
            aliases[stripped] = key;
    };

    (columnKeys || []).forEach(key => {
        add(key, key);
        if (legacyLabels[key])
            add(legacyLabels[key], key);
        (langs || []).forEach(lang => {
            const label = labelFor?.(key, lang);
            if (label)
                add(label, key);
        });
    });

    (extraAliases || []).forEach(([label, key]) => add(label, key));

    return aliases;
};

/**
 * Attach Excel hover comments on the header row (row 0).
 *
 * @param {object} ws SheetJS worksheet
 * @param {string[]} columnKeys Column order matching the sheet
 * @param {(key: string) => string} helpFor Returns comment text (empty = skip)
 * @param {string} [author]
 */
export const annotateHeaderComments = (ws, columnKeys, helpFor, author = 'Ticketack') => {
    if (!ws || !columnKeys?.length || typeof helpFor !== 'function')
        return;

    columnKeys.forEach((key, index) => {
        const addr = XLSX.utils.encode_cell({ r: 0, c: index });
        const cell = ws[addr];
        if (!cell)
            return;

        const comment = helpFor(key);
        if (!comment || comment === key)
            return;

        cell.c = [{ t: comment, a: author }];
        // Show on hover only (Excel opens comments otherwise).
        cell.c.hidden = true;
    });
};
